<!--
  LazyLoadWhenInViewport is a component that defers the rendering of its default slot content
  until the component enters the viewport. It shows a fallback content until then.

  **** WARNING ****
  When using this component to render different content on the server versus the client
  (e.g., by controlling `disableObserver` based on SSR context), you MUST add the `data-allow-mismatch`
  attribute to the component's root element in the parent to prevent hydration errors.
-->
<template>
  <!-- shared / reusable template -->
  <DefineDefaultTemplate>
    <component :is="tag" ref="targetEl" v-bind="attrs" class="lazy-component-wrapper">
      <slot v-if="inViewport"></slot>
      <slot v-else name="fallback"></slot>
    </component>
  </DefineDefaultTemplate>

  <!-- wraps transition around observer template  -->
  <Transition v-if="withTransition" name="fade" mode="out-in">
    <ReuseDefaultTemplate :key="String(inViewport)" />
  </Transition>
  <!-- default template  -->
  <template v-else>
    <ReuseDefaultTemplate />
  </template>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef, useAttrs } from 'vue';
import { useIntersectionObserver, createReusableTemplate } from '@vueuse/core';
const attrs = useAttrs();

/**
 * createReusableTemplate allows defining a template structure once and reusing it multiple times.
 * This is used here to conditionally wrap the main content with a <Transition> component
 * without duplicating the template logic.
 */
const [DefineDefaultTemplate, ReuseDefaultTemplate] = createReusableTemplate();

type LazyInterscrtionProps = {
  /** The margin around the root. Can be a positive or negative value. */
  rootMargin?: number;
  /** The HTML tag to be used for the root element. */
  tag?: keyof HTMLElementTagNameMap;
  /** If true, the intersection observer is disabled and the default slot is rendered immediately. */
  disableObserver?: boolean;
  /** If true, wraps the content in a fade transition. */
  withTransition?: boolean;
};

const {
  /** The distance from the viewport edge at which to trigger the intersection observer. */
  rootMargin = 600,
  /** The HTML element tag for the wrapper component. */
  tag = 'div',
  /** Disables the observer, forcing immediate rendering of the default slot. Useful for SSR. */
  disableObserver = false,
  /** Enables a fade-in transition when the content loads. */
  withTransition = false,
} = defineProps<LazyInterscrtionProps>();

/**
 * useTemplateRef is a utility from VueUse that provides a typed template ref,
 * which is especially useful within reusable templates created by createReusableTemplate.
 */
const targetEl = useTemplateRef<HTMLElement>('targetEl');

/** A reactive flag that becomes true when the component enters the viewport. */
const inViewport = ref<boolean>(disableObserver);

if (!disableObserver) {
  const { stop } = useIntersectionObserver(
    targetEl,
    (entries) => {
      const isIntersecting = entries[0]?.isIntersecting as boolean;
      // When the component comes into view, set inViewport to true and stop observing.
      if (isIntersecting) {
        inViewport.value = true;
        stop();
      }
    },
    {
      rootMargin: `${rootMargin}px`,
    },
  );
}
</script>
<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
